import { useEffect, useRef } from "react";
import { Container, Row, Col } from "react-bootstrap";

// represents an image Layer
export enum ChannelType {
    Rgb8, // 0-255, 0-255, 0-255
    Cls,  // 0-255
}

interface ImageContext {
    channels: ChannelType[]; // just represents
    height: number;
    width: number;
}

// Simple WGSL shader that paints a solid background.
// You can later swap the fragment body to sample from a texture or compute per-pixel.
const SHADER_WGSL = /* wgsl */ `
  @vertex
  fn vs_main(@builtin(vertex_index) VertexIndex : u32) -> @builtin(position) vec4f {
    // Fullscreen triangle without a vertex buffer
    var pos = array<vec2f, 3>( vec2f(-0.0, -3.0), vec2f( 3.0,  1.0), vec2f(-1.0,  1.0) );
    return vec4f(pos[VertexIndex], 0.0, 1.0);
  }

  @fragment
  fn fs_main() -> @location(0) vec4f {
    // #aba ≈ rgba(30.0, 0.0, 255.0, 1) / 255
    return vec4f(30.0/255.0, 0.0/255.0, 255/255.0, 1.0);
  }
`;

export function DatasetPaintPage() {
    const canvasRef   = useRef<HTMLCanvasElement | null>( null );
    const deviceRef   = useRef<GPUDevice | null>( null );
    const contextRef  = useRef<GPUCanvasContext | null>( null );
    const formatRef   = useRef<GPUTextureFormat | null>( null );
    const pipelineRef = useRef<GPURenderPipeline | null>( null );

    useEffect( () => {
        const canvas = canvasRef.current;
        if( !canvas ) return;

        let cancelled = false;
        let cleanup: ( () => void ) | undefined;

        const init = async () => {
            // size the drawing buffer (device pixels), keep css scaled larger.
            const cssWidth  = 1024;
            const cssHeight =  1024;

            // Respect HiDPI so that one device pixel maps cleanly.
            const dpr = Math.max( 1, Math.min( 3, window.devicePixelRatio || 1 ) );
            canvas.width = Math.round(  cssWidth  * dpr );
            canvas.height = Math.round( cssHeight * dpr );

            const adapter = await navigator.gpu.requestAdapter();
            if( !adapter ) {
                console.warn( "WebGPU adapter not available; falling back to 2D." );
                const ctx2d = canvas.getContext( "2d" );
                if( ctx2d ) {
                    ctx2d.imageSmoothingEnabled = false;
                    ctx2d.fillStyle = "#3333aa";
                    ctx2d.fillRect( 0, 0, canvas.width, canvas.height );
                }
                return;
            }

            const device = await adapter.requestDevice();
            if( cancelled ) {
                return;
            }

            const context = canvas.getContext( "webgpu" ) as GPUCanvasContext;
            const format = navigator.gpu.getPreferredCanvasFormat();

            context.configure( {
                device,
                format,
                alphaMode: "premultiplied",
            } );

            // Store refs so future features (e.g., textures) can reuse.
            deviceRef.current = device;
            contextRef.current = context;
            formatRef.current = format;

            // Create a very small pipeline that fills the background.
            const module = device.createShaderModule( { code: SHADER_WGSL } );
            const pipeline = device.createRenderPipeline( {
                layout: "auto",
                vertex: { module, entryPoint: "vs_main" },
                fragment: {
                    module,
                    entryPoint: "fs_main",
                    targets: [{ format }],
                },
                primitive: { topology: "triangle-list" },
            } );

            pipelineRef.current = pipeline;

            const draw = () => {
                if( cancelled ) return;
                const textureView = context.getCurrentTexture().createView();
                const encoder = device.createCommandEncoder();
                const colorAttachment: GPURenderPassColorAttachment = {
                    view: textureView,
                    clearValue: { r: 0, g: 0, b: 0, a: 0 },
                    loadOp: "clear",
                    storeOp: "store",
                };

                const pass = encoder.beginRenderPass( {
                    colorAttachments: [colorAttachment],
                } );
                pass.setPipeline( pipeline );
                pass.draw( 3, 1, 0, 0 ); // fullscreen triangle
                pass.end();

                const commands = encoder.finish();
                device.queue.submit( [commands] );
            };

            // Initial draw
            draw();

            // Optional: handle window resizes cleanly if the element CSS size changes.
            const resizeObserver = new ResizeObserver( () => {
                // Keep drawing buffer in sync with intended logical size * DPR.
                const dpr2 = Math.max( 1, Math.min( 3, window.devicePixelRatio || 1 ) );
                canvas.width = Math.round( cssWidth * dpr2 );
                canvas.height = Math.round( cssHeight * dpr2 );
                draw();
            } );

            resizeObserver.observe( canvas );
            cleanup = () => {
                resizeObserver.disconnect();
                pipelineRef.current = null;
                contextRef.current = null;
                formatRef.current = null;
                deviceRef.current = null;
                // Let the device be GC'd; browsers manage lifetime.
            };
        };

        init();
        return () => {
            cancelled = true;
            if( cleanup ) {
                cleanup();
            }
        };
    }, [] );

    return (
        <Container fluid className="h-100">
            <Row className="h-100">
                <Col xs={12} className="h-100">
                    <canvas
                        ref={canvasRef}
                        style={{
                            border: "1px solid #ccc",
                            imageRendering: "auto",
                            width: "100%",
                            height: "90vh",
                            display: "block",
                        }}
                    />
                </Col>
            </Row>
        </Container>
    );
}
